import { useEffect, useRef, useState } from "react";
import type { Editor } from "@tiptap/react";
import type { Selection } from "@tiptap/pm/state";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import { BaseInput, InputSize } from "shared/components/forms/BaseInput";

type ModalMode = "view" | "add" | "edit";

export interface NoteLinkModalState {
  isOpen: boolean;
  mode: ModalMode;
  initialText: string;
  initialUrl: string;
  savedSelection: Selection | null;
  savedText: string;
}

const modalTitles: Record<ModalMode, string> = {
  view: "Link text",
  add: "Add link",
  edit: "Edit link"
};

interface NoteLinkModalProps {
  editor: Editor | null;
  state: NoteLinkModalState;
  onClose: () => void;
}

export const NoteLinkModal = ({
  editor,
  state,
  onClose
}: NoteLinkModalProps) => {
  const [mode, setMode] = useState<ModalMode>("add");
  const [editText, setEditText] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const savedSelection = useRef<Selection | null>(null);
  const savedText = useRef("");
  const textInputRef = useRef<HTMLInputElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!state.isOpen) return;
    savedSelection.current = state.savedSelection;
    savedText.current = state.savedText;
    setEditText(state.initialText);
    setEditUrl(state.initialUrl);
    setMode(state.mode);
  }, [
    state.isOpen,
    state.mode,
    state.initialText,
    state.initialUrl,
    state.savedSelection,
    state.savedText
  ]);

  useEffect(() => {
    if (mode === "view") return;
    if (savedText.current && editUrl) return;
    if (savedText.current) {
      urlInputRef.current?.focus();
      return;
    }
    textInputRef.current?.focus();
  }, [mode, editUrl]);

  if (!editor) return null;

  const handleEditClick = () => {
    const selection = savedSelection.current ?? editor.state.selection;
    savedSelection.current = selection;
    savedText.current = editor.state.doc.textBetween(
      selection.from,
      selection.to,
      ""
    );
    setEditText(savedText.current || state.initialText);
    setEditUrl(state.initialUrl);
    setMode("edit");
  };

  const handleApply = () => {
    const text = editText.trim();
    const url = editUrl.trim();
    const sel = savedSelection.current;
    const href = url && !url.startsWith("http") ? `https://${url}` : url;
    const hasSelection = Boolean(sel && sel.from !== sel.to);
    const textChanged = text !== savedText.current;

    if (!url) {
      if (hasSelection && sel)
        editor
          .chain()
          .focus()
          .setTextSelection({ from: sel.from, to: sel.to })
          .unsetLink()
          .run();
    } else if (hasSelection && sel && !textChanged)
      editor
        .chain()
        .focus()
        .setTextSelection({ from: sel.from, to: sel.to })
        .setLink({ href })
        .run();
    else if (hasSelection && sel && textChanged && text)
      editor
        .chain()
        .focus()
        .setTextSelection({ from: sel.from, to: sel.to })
        .insertContent({
          type: "text",
          text,
          marks: [{ type: "link", attrs: { href } }]
        })
        .run();
    else {
      const insertText = text || href;
      editor
        .chain()
        .focus()
        .insertContent({
          type: "text",
          text: insertText,
          marks: [{ type: "link", attrs: { href } }]
        })
        .run();
    }

    savedSelection.current = null;
    savedText.current = "";
    onClose();
  };

  const handleRemove = () => {
    editor.chain().focus().unsetLink().run();
    onClose();
  };

  const handleCancel = () => {
    if (mode === "edit" && state.mode === "view") {
      setMode("view");
      return;
    }
    editor.chain().focus().run();
    savedSelection.current = null;
    savedText.current = "";
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApply();
    }
    if (e.key === "Escape") handleCancel();
  };

  return (
    <Modal
      opened={state.isOpen}
      onClose={onClose}
      panelClassName="max-w-[328px] w-full p-5"
      zIndex={9999}
    >
      <div className="flex items-center justify-between mb-4">
        <p className="text-base font-bold text-content-1000">
          {modalTitles[mode]}
        </p>
        <IconButton
          variant={IconButtonVariant.TEXT_BASE}
          size={IconButtonSize.SM}
          HugeIcon={Cancel01Icon}
          title="Close"
          onClick={onClose}
        />
      </div>

      {mode === "view" ? (
        <>
          <a
            href={state.initialUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-base font-medium text-primary-500 break-all block mb-4"
          >
            {state.initialUrl}
          </a>
          <div className="flex gap-2">
            <Button
              size={ButtonSize.BASE}
              className="w-full"
              onClick={handleEditClick}
            >
              Edit
            </Button>
            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.DANGER}
              className="w-full"
              onClick={handleRemove}
            >
              Remove
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col gap-3 mb-4">
            <BaseInput
              id="note-link-modal-text"
              ref={textInputRef}
              size={InputSize.BASE}
              value={editText}
              onChange={setEditText}
              onKeyDown={handleKeyDown}
              placeholder="Add text"
              label="Text"
              required
            />
            <BaseInput
              id="note-link-modal-url"
              ref={urlInputRef}
              size={InputSize.BASE}
              type="url"
              value={editUrl}
              onChange={setEditUrl}
              onKeyDown={handleKeyDown}
              placeholder="Add link"
              label="Link"
              required
            />
          </div>
          <div className="flex gap-2">
            <Button
              size={ButtonSize.BASE}
              className="w-full"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.PRIMARY}
              className="w-full"
              onClick={handleApply}
            >
              Save
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};
